<template>
	<div>
		<div class="flex align-items-center justify-content-between mb-2">
			<WSubTitle text="Action queue" />
		</div>

		<div class="action-chips mb-3">
			<button
				v-if="showManual"
				class="action-chip"
				type="button"
				@click="go('manual-entries')"
			>
				<span class="action-count" :class="{ hot: (actions?.pending_manual_entries || 0) > 0 }">
					{{ actions?.pending_manual_entries ?? 0 }}
				</span>
				<span>Pending manual entries</span>
			</button>
			<button
				v-if="showInvoice"
				class="action-chip"
				type="button"
				@click="$emit('focus-settlement')"
			>
				<span class="action-count" :class="{ hot: (actions?.draft_invoices || 0) > 0 }">
					{{ actions?.draft_invoices ?? 0 }}
				</span>
				<span>Draft invoices</span>
			</button>
			<button
				v-if="showPpm"
				class="action-chip"
				type="button"
				@click="go('performance')"
			>
				<span class="action-count" :class="{ hot: (actions?.invalid_hours || 0) > 0 }">
					{{ actions?.invalid_hours ?? 0 }}
				</span>
				<span>Invalid hours</span>
			</button>
			<div
				v-if="showTelemetry"
				class="action-chip static"
			>
				<span class="action-count" :class="{ hot: ((actions?.telemetry_pending || 0) + (actions?.telemetry_failed || 0)) > 0 }">
					{{ (actions?.telemetry_pending || 0) + (actions?.telemetry_failed || 0) }}
				</span>
				<span>Telemetry backlog</span>
			</div>
		</div>

		<div v-if="!hasAnyDetail" class="text-sm text-color-secondary">
			Nothing waiting for attention.
		</div>

		<div v-if="showManual && actions?.manual_entries_recent?.length" class="mb-3">
			<div class="section-label mb-2">Recent pending entries</div>
			<ul class="detail-list">
				<li v-for="e in actions.manual_entries_recent" :key="e.id" class="detail-row">
					<div>
						<strong>{{ e.variable_name }}</strong>
						<span class="text-color-secondary"> · {{ e.period_name }}</span>
						<span v-if="e.value != null"> · {{ e.value }}</span>
						<span class="text-color-secondary"> · {{ e.submitted_by_username || '—' }}</span>
					</div>
					<div v-if="canApproveManual" class="flex gap-1">
						<PButton
							:disabled="busyId === e.id"
							:loading="busyId === e.id && busyAction === 'approve'"
							icon="pi pi-check"
							rounded
							size="small"
							severity="success"
							v-tooltip.top="'Approve'"
							@click="$emit('approve-entry', e.id)"
						/>
						<PButton
							:disabled="busyId === e.id"
							:loading="busyId === e.id && busyAction === 'reject'"
							icon="pi pi-times"
							outlined
							rounded
							size="small"
							severity="danger"
							v-tooltip.top="'Reject'"
							@click="$emit('reject-entry', e.id)"
						/>
					</div>
				</li>
			</ul>
		</div>

		<div v-if="showInvoice && actions?.draft_invoices_list?.length" class="mb-3">
			<div class="section-label mb-2">Draft invoices</div>
			<ul class="detail-list">
				<li v-for="inv in actions.draft_invoices_list" :key="inv.id" class="detail-row">
					<div>
						<strong>{{ inv.invoice_number || ('#' + inv.id) }}</strong>
						<span class="text-color-secondary"> · {{ inv.mode }} · {{ inv.generated_by || '—' }}</span>
					</div>
					<PButton
						v-if="canApproveInvoice"
						:disabled="busyId === inv.id"
						:loading="busyId === inv.id && busyAction === 'approve-inv'"
						icon="pi pi-check"
						label="Approve"
						rounded
						size="small"
						severity="success"
						@click="$emit('approve-invoice', inv.id)"
					/>
				</li>
			</ul>
		</div>

		<div v-if="showPpm && actions?.invalid_hours_recent?.length">
			<div class="section-label mb-2">Recent invalid hours</div>
			<ul class="detail-list">
				<li v-for="(h, idx) in actions.invalid_hours_recent" :key="idx">
					<strong>{{ h.hour_start || '—' }}</strong>
					<span class="text-color-secondary"> · {{ h.invalid_reason || 'invalid' }}</span>
				</li>
			</ul>
		</div>
	</div>
</template>

<script lang="ts">
	import { defineComponent, PropType } from 'vue'
	import store from '@/store'
	import { DashboardActions } from '@/brokers/DashboardBroker'

	export default defineComponent({
		name: 'DashActionQueue',
		props: {
			actions: { type: Object as PropType<DashboardActions | null>, default: null },
			busyId: { type: [Number, String] as PropType<number | string | null>, default: null },
			busyAction: { type: String, default: '' },
		},
		emits: ['approve-entry', 'reject-entry', 'approve-invoice', 'focus-settlement'],
		computed: {
			showManual(): boolean {
				return store.getters.can('manual_entries', 'read')
			},
			canApproveManual(): boolean {
				return store.getters.can('manual_entries', 'approve')
			},
			showInvoice(): boolean {
				return store.getters.can('invoice', 'read') || store.getters.can('invoice', 'approve')
			},
			canApproveInvoice(): boolean {
				return store.getters.can('invoice', 'approve')
			},
			showPpm(): boolean {
				return store.getters.can('ppm', 'read')
			},
			showTelemetry(): boolean {
				return store.getters.can('telemetry', 'read')
			},
			hasAnyDetail(): boolean {
				const a = this.actions
				if (!a) return false
				return Boolean(
					(this.showManual && a.manual_entries_recent?.length)
					|| (this.showInvoice && a.draft_invoices_list?.length)
					|| (this.showPpm && a.invalid_hours_recent?.length),
				)
			},
		},
		methods: {
			go(name: string) {
				this.$router.push({ name }).catch(() => undefined)
			},
		},
	})
</script>

<style scoped>
	.action-chips {
		display: flex;
		flex-wrap: wrap;
		gap: 0.5rem;
	}
	.action-chip {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		border: 1px solid var(--surface-border);
		background: var(--surface-ground);
		border-radius: 6px;
		padding: 0.5rem 0.75rem;
		cursor: pointer;
		font: inherit;
		color: inherit;
	}
	.action-chip:hover:not(.static) {
		border-color: var(--primary-color);
	}
	.action-chip.static {
		cursor: default;
	}
	.action-count {
		min-width: 1.5rem;
		height: 1.5rem;
		border-radius: 4px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: var(--surface-border);
		font-weight: 700;
		font-size: 0.85rem;
	}
	.action-count.hot {
		background: var(--orange-100, #ffedd5);
		color: var(--orange-700, #c2410c);
	}
	.section-label {
		font-size: 0.85rem;
		font-weight: 600;
	}
	.detail-list {
		margin: 0;
		padding-left: 0;
		list-style: none;
		font-size: 0.875rem;
	}
	.detail-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 0.75rem;
		padding: 0.35rem 0;
		border-bottom: 1px solid var(--surface-border);
	}
</style>
